import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';

const SEGMENT_COLORS = {
  hot: '#10B981',
  warm: '#2563EB',
  cool: '#F59E0B',
  cold: '#64748b',
  dead: '#3f1f1f',
  unknown: '#334155'
};

function fmt$(n) {
  if (n == null) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function fmtDate(iso) {
  if (!iso) return '—';
  // Parse date string directly to avoid UTC→local timezone shift
  // Handles both "2026-04-05" and "2026-04-05T00:00:00.000Z"
  const str = typeof iso === 'string' ? iso : iso.toISOString ? iso.toISOString() : String(iso);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '—';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(match[2], 10) - 1]} ${parseInt(match[3], 10)}`;
}

function TrendBadge({ pct }) {
  if (pct == null || pct === 0) return <span style={{ color: 'var(--muted)', fontSize: 12 }}>—</span>;
  const pos = pct > 0;
  return (
    <span style={{ color: pos ? '#10B981' : '#EF4444', fontSize: 12, fontWeight: 600 }}>
      {pos ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
}

function PaymentBadge({ processor }) {
  const label = processor || 'Unknown';
  const isStripe = label === 'Stripe';
  const isPayPal = label === 'PayPal';
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 58,
      padding: '3px 8px',
      borderRadius: 999,
      border: `1px solid ${isStripe ? 'rgba(99,102,241,0.35)' : isPayPal ? 'rgba(37,99,235,0.35)' : 'var(--border)'}`,
      background: isStripe ? 'rgba(99,102,241,0.12)' : isPayPal ? 'rgba(37,99,235,0.12)' : 'rgba(100,116,139,0.08)',
      color: isStripe ? '#A5B4FC' : isPayPal ? '#93C5FD' : 'var(--muted)',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.02em'
    }}>
      {label}
    </span>
  );
}

function AttributionBadge({ tx }) {
  const label = tx?.attribution_label || 'Direct / Unknown';
  const cls = tx?.attribution_class || 'direct_unknown';
  const colors = {
    direct_unknown: { border: 'rgba(245,158,11,0.35)', bg: 'rgba(245,158,11,0.12)', text: '#FBBF24' },
    broadcast_email: { border: 'rgba(16,185,129,0.35)', bg: 'rgba(16,185,129,0.12)', text: '#6EE7B7' },
    triggered_email: { border: 'rgba(37,99,235,0.35)', bg: 'rgba(37,99,235,0.12)', text: '#93C5FD' },
    sequence_email: { border: 'rgba(168,85,247,0.35)', bg: 'rgba(168,85,247,0.12)', text: '#C4B5FD' }
  };
  const c = colors[cls] || { border: 'var(--border)', bg: 'rgba(100,116,139,0.08)', text: 'var(--muted)' };
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3px 8px',
      borderRadius: 999,
      border: `1px solid ${c.border}`,
      background: c.bg,
      color: c.text,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.02em',
      whiteSpace: 'nowrap'
    }}>
      {label}
    </span>
  );
}

function KPICard({ label, value, sub, trend }) {
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '20px 24px',
      flex: 1,
      minWidth: 140
    }}>
      <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text)', lineHeight: 1.1 }}>{value}</div>
      {(sub || trend != null) && (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          {sub && <span style={{ color: 'var(--muted)', fontSize: 12 }}>{sub}</span>}
          {trend != null && <TrendBadge pct={trend} />}
        </div>
      )}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: '#1a2236', border: '1px solid #1e2d45', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
      <div style={{ color: '#94a3b8', marginBottom: 4 }}>{fmtDate(label)}</div>
      <div style={{ color: '#10B981', fontWeight: 700 }}>{fmt$(payload[0]?.value)}</div>
      {payload[1] && <div style={{ color: '#64748b', fontSize: 12 }}>{payload[1]?.value} orders</div>}
    </div>
  );
};

export default function Revenue() {
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`/api/analytics/revenue?range=${range}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [range]);

  const s = data?.summary || {};

  const ranges = ['7d', '30d', '90d', 'all'];
  const rangeLabels = { '7d': '7D', '30d': '30D', '90d': '90D', 'all': 'All Time' };

  return (
    <div style={{ padding: '28px 0' }}>
      {/* Header + Range Selector */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Revenue</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 13 }}>
            All purchases processed through MemberPress
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {ranges.map(r => (
            <button
              key={r}
              onClick={() => setRange(r)}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: '1px solid var(--border)',
                background: range === r ? 'var(--blue)' : 'var(--surface)',
                color: range === r ? '#fff' : 'var(--muted)',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: range === r ? 600 : 400
              }}
            >{rangeLabels[r]}</button>
          ))}
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 80 }}>Loading revenue data...</div>
      )}

      {error && (
        <div style={{ background: '#1a0a0a', border: '1px solid #3f1a1a', borderRadius: 8, padding: 16, color: '#EF4444', marginBottom: 20 }}>
          Error: {error}
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 28, flexWrap: 'wrap' }}>
            <KPICard
              label="Total Revenue"
              value={fmt$(s.total_revenue)}
              sub={`vs ${fmt$(s.prev_total_revenue)} prior`}
              trend={s.revenue_trend_pct}
            />
            <KPICard label="Total Orders" value={s.total_orders?.toLocaleString() || '0'} />
            <KPICard label="Unique Buyers" value={s.unique_buyers?.toLocaleString() || '0'} />
            <KPICard label="Avg Order Value" value={fmt$(s.aov)} />
            <KPICard label="Revenue Today" value={fmt$(s.revenue_today)} />
          </div>

          {/* Revenue Trend Chart */}
          <div style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '20px 24px',
            marginBottom: 28
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
              Revenue Trend
            </div>
            {data.daily_revenue?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data.daily_revenue} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2d45" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={fmtDate}
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={v => `$${v.toLocaleString()}`}
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={70}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#10B981"
                    strokeWidth={2}
                    fill="url(#revenueGrad)"
                    dot={false}
                    activeDot={{ r: 4, fill: '#10B981' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>No revenue data in this period</div>
            )}
          </div>

          {/* Revenue by Attribution */}
          <div style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '20px 24px',
            marginBottom: 28
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                Revenue by Attribution
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                Direct / Unknown = no tracked ECC opt-in, click, or sequence before purchase
              </div>
            </div>
            {data.by_attribution?.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Source', 'Orders', 'Revenue', '% Total'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Source' ? 'left' : 'right', color: 'var(--muted)', fontWeight: 500, fontSize: 12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.by_attribution.map((src, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(30,45,69,0.5)' }}>
                      <td style={{ padding: '10px 10px' }}>
                        <AttributionBadge tx={{ attribution_label: src.attribution_label, attribution_class: src.attribution_class }} />
                      </td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', color: 'var(--muted)' }}>{src.orders}</td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', color: '#10B981', fontWeight: 600 }}>{fmt$(src.revenue)}</td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', color: 'var(--muted)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                          <div style={{
                            height: 4, width: Math.max(src.pct_of_total, 2) * 0.6,
                            background: src.attribution_class === 'direct_unknown' ? '#F59E0B' : '#10B981', borderRadius: 2, opacity: 0.6
                          }} />
                          {src.pct_of_total}%
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>No attribution data available</div>
            )}
          </div>

          {/* By Product + By Segment */}
          <div style={{ display: 'flex', gap: 20, marginBottom: 28, alignItems: 'flex-start' }}>
            {/* Revenue by Product */}
            <div style={{
              flex: '0 0 60%',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '20px 24px'
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
                Revenue by Product
              </div>
              {data.by_product?.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Product', 'Orders', 'Revenue', 'AOV', '% Total'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Product' ? 'left' : 'right', color: 'var(--muted)', fontWeight: 500, fontSize: 12 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_product.map((p, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(30,45,69,0.5)' }}>
                        <td style={{ padding: '10px 10px', color: p.product_name === 'Unknown' ? 'var(--muted)' : 'var(--text)', fontStyle: p.product_name === 'Unknown' ? 'italic' : 'normal' }}>
                          {p.product_name}
                        </td>
                        <td style={{ padding: '10px 10px', textAlign: 'right', color: 'var(--muted)' }}>{p.orders}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right', color: '#10B981', fontWeight: 600 }}>{fmt$(p.revenue)}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right', color: 'var(--muted)' }}>{fmt$(p.aov)}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right', color: 'var(--muted)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                            <div style={{
                              height: 4, width: Math.max(p.pct_of_total, 2) * 0.6,
                              background: '#10B981', borderRadius: 2, opacity: 0.6
                            }} />
                            {p.pct_of_total}%
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ color: 'var(--muted)', fontSize: 13 }}>No product data available</div>
              )}
            </div>

            {/* Revenue by Segment */}
            <div style={{
              flex: 1,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '20px 24px'
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
                Revenue by Segment
              </div>
              {data.by_segment?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {data.by_segment.map((seg, i) => (
                    <div key={i} style={{
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: '12px 14px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{
                          color: SEGMENT_COLORS[seg.segment] || '#64748b',
                          fontWeight: 600,
                          fontSize: 13,
                          textTransform: 'capitalize'
                        }}>
                          {seg.segment}
                          <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 12, marginLeft: 6 }}>
                            ({seg.subscriber_count?.toLocaleString()} subs)
                          </span>
                        </span>
                        <span style={{ color: '#10B981', fontWeight: 700, fontSize: 14 }}>{fmt$(seg.revenue)}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--muted)' }}>
                        <span>{seg.orders} order{seg.orders !== 1 ? 's' : ''}</span>
                        <span>Rev/Sub: {seg.rev_per_sub > 0 ? fmt$(seg.rev_per_sub) : '—'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: 'var(--muted)', fontSize: 13 }}>No segment data available</div>
              )}
            </div>
          </div>

          {/* Recent Transactions */}
          <div style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '20px 24px'
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
              Recent Transactions
            </div>
            {data.recent_transactions?.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Date', 'Buyer', 'Amount', 'Payment', 'Product', 'Segment', 'Source', 'Attribution Detail'].map(h => (
                      <th key={h} style={{
                        padding: '8px 10px',
                        textAlign: h === 'Amount' ? 'right' : 'left',
                        color: 'var(--muted)',
                        fontWeight: 500,
                        fontSize: 12
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.recent_transactions.map((tx, i) => (
                    <tr
                      key={i}
                      style={{
                        borderBottom: '1px solid rgba(30,45,69,0.5)',
                        borderLeft: tx.amount >= 500 ? '3px solid #F59E0B' : '3px solid transparent',
                        background: tx.amount >= 500 ? 'rgba(245,158,11,0.04)' : 'transparent'
                      }}
                    >
                      <td style={{ padding: '10px 10px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                        {fmtDate(tx.processed_at)}
                      </td>
                      <td style={{ padding: '10px 10px', color: 'var(--text)', fontFamily: 'monospace', fontSize: 12 }}>
                        {tx.buyer_email}
                      </td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', color: '#10B981', fontWeight: 700 }}>
                        {fmt$(tx.amount)}
                      </td>
                      <td style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>
                        <PaymentBadge processor={tx.payment_processor} />
                      </td>
                      <td style={{ padding: '10px 10px', color: tx.product_name === 'Unknown' ? 'var(--muted)' : 'var(--text)', fontStyle: tx.product_name === 'Unknown' ? 'italic' : 'normal' }}>
                        {tx.product_name}
                      </td>
                      <td style={{ padding: '10px 10px' }}>
                        <span style={{
                          color: SEGMENT_COLORS[tx.segment] || '#64748b',
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}>{tx.segment}</span>
                      </td>
                      <td style={{ padding: '10px 10px' }}>
                        <AttributionBadge tx={tx} />
                      </td>
                      <td style={{ padding: '10px 10px', color: 'var(--muted)', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tx.attribution_detail || tx.attributed_draft_subject || <span style={{ opacity: 0.4 }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>No transactions in this period</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
